import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormValidators from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import { PDFHighlight } from "@/components/PDFViewer/PDFViewer";
import i18n from "@/i18n";
import BFSection from "@/modules/abstract-ui/data/section/BFSection";
import BFCheckbox from "@/modules/abstract-ui/forms/checkbox/BFCheckbox";
import BFDatefield from "@/modules/abstract-ui/forms/datefield/BFDatefield";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFDropdown from "@/modules/abstract-ui/general/Dropdown/BFDropdown";
import { DropdownButton } from "@/modules/abstract-ui/general/Dropdown/BFDropdownContent";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import { DefaultIcons } from "@/modules/abstract-ui/icon/DefaultIcons";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import { isDefined } from "@/utils/Helpers";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { FormApi } from "final-form";
import { useState } from "react";
import { FieldArray } from "react-final-form-arrays";
import {
  Invoice,
  PaymentEntry,
  SkontoFields,
  SkontoPaymentFormEntry,
} from "../../../RAInterfaces";
import { ExtractField } from "../ExtractField";
import { RAInvoiceCostcenterForm } from "../RAInvoiceCostcenterForm";
import { INVOICE_EXTRACT_KEYS } from "../RAInvoiceFormConst";
import "./RAInvoicePaymentFields.scss";
import RAInvoicePaymentSkontoForm from "./RAInvoicePaymentSkontoForm";
import RAInvoicePaymentSplitForm from "./RAInvoicePaymentSplitForm";
import RAInvoicePaymentUsageEditForm from "./RAInvoicePaymentUsageEditForm";

interface RAInvoicePaymentFieldsProps {
  disableAll?: boolean;
  ignoreCompare?: boolean;
  prefix?: string;
  invoice: Invoice;
  form: FormApi;
  onHighlight: (highlight?: PDFHighlight | PDFHighlight[]) => void;

  uploadForm?: boolean;
}
const RAInvoicePaymentFields = (props: RAInvoicePaymentFieldsProps) => {
  const [initialData] = useState([
    {
      amount: 0,
      date: new Date(),
      usage: "",
    },
  ]);
  return (
    <FormFieldValues
      names={[
        `${props.prefix || ""}currency`,
        `${props.prefix || ""}paymentType`,
      ]}
    >
      {([currency, paymentType]) => {
        const modeOfPayment = InvoiceStruct.getModeOfPayment(
          props.invoice.data.direction,
          paymentType
        );
        const paymentTypeDisabledEdit =
          modeOfPayment && modeOfPayment.kind === "bankTransfer"
            ? !Boolean(modeOfPayment?.editable)
            : false;
        return (
          <div className={classNames("ra-invoice-payment-fields")}>
            <div className={`payent-data`}>
              <FieldArray<SkontoPaymentFormEntry>
                name={`${props.prefix || ""}paymentPlan`}
                initialValue={initialData as any}
              >
                {({ fields }) => {
                  if (fields.length === 1) {
                    return (
                      <FormFieldValues
                        names={[
                          `${props.prefix || ""}paymentPlan[0].payedDate`,
                        ]}
                      >
                        {([payedDate]) => (
                          <div className={`payment-entry`}>
                            <div className={`form-row`}>
                              <div className={`field __flex-1`}>
                                <ExtractField
                                  ignoreCompare={props.ignoreCompare}
                                  name={`${
                                    props.prefix || ""
                                  }paymentPlan[0].amount`}
                                  validate={FormValidators.compose(
                                    FormValidators.required()
                                  )}
                                  formatValue={(value: string) =>
                                    StringUtils.formatCurrency(
                                      Number(value),
                                      undefined,
                                      undefined,
                                      props.invoice.extract?.currencyCode
                                    )
                                  }
                                  invoice={props.invoice}
                                  onHighlight={props.onHighlight}
                                  field={
                                    INVOICE_EXTRACT_KEYS.paymentAmountTotal
                                  }
                                >
                                  {({ input, meta }) => (
                                    <BFInput
                                      disabled={
                                        Boolean(payedDate) || props.disableAll
                                      }
                                      prefix={StringUtils.getCurrencySymbol(
                                        currency
                                      )}
                                      type={"priceInput"}
                                      label={
                                        i18n.t(
                                          "ra:InvoiceForm.invoiceAmount",
                                          "Rechnungsbetrag"
                                        ) + "*"
                                      }
                                      {...FormValidators.getValidation(meta)}
                                      {...input}
                                    />
                                  )}
                                </ExtractField>
                              </div>
                              <div className={`field __flex-1`}>
                                <ExtractField
                                  ignoreCompare={props.ignoreCompare}
                                  name={`${
                                    props.prefix || ""
                                  }paymentPlan[0].date`}
                                  validate={FormValidators.compose(
                                    FormValidators.required()
                                  )}
                                  invoice={props.invoice}
                                  onHighlight={props.onHighlight}
                                  field={INVOICE_EXTRACT_KEYS.paymentDateDue}
                                >
                                  {({ input, meta }) => (
                                    <BFDatefield
                                      disabled={
                                        Boolean(payedDate) ||
                                        props.disableAll ||
                                        paymentTypeDisabledEdit
                                      }
                                      label={
                                        i18n.t(
                                          "ra:InvoiceForm.dueDate",
                                          "Fälligkeitsdatum"
                                        ) + "*"
                                      }
                                      preventNull
                                      {...FormValidators.getValidation(meta)}
                                      {...input}
                                    />
                                  )}
                                </ExtractField>
                              </div>
                              <div className={`field __flex-1`}>
                                <ExtractField
                                  ignoreCompare={props.ignoreCompare}
                                  name={`${
                                    props.prefix || ""
                                  }paymentPlan[0].usage`}
                                  validate={FormValidators.compose(
                                    FormValidators.required()
                                  )}
                                  invoice={props.invoice}
                                  onHighlight={props.onHighlight}
                                  field={INVOICE_EXTRACT_KEYS.invoiceNumber}
                                >
                                  {({ input, meta }) => (
                                    <BFInput
                                      disabled={
                                        Boolean(payedDate) || props.disableAll
                                      }
                                      label={
                                        i18n.t(
                                          "ra:InvoiceForm.usage",
                                          "Verwendungszweck"
                                        ) + "*"
                                      }
                                      {...FormValidators.getValidation(meta)}
                                      {...input}
                                    />
                                  )}
                                </ExtractField>
                              </div>
                              <div className={`button-container`}>
                                <FormFieldValues
                                  names={[
                                    `${props.prefix || ""}objectId`,
                                    `${props.prefix || ""}entity`,
                                    `${props.prefix || ""}invoiceId`,
                                  ]}
                                >
                                  {([objectId, entity, invoiceId]) => (
                                    <BFDropdown
                                      label="."
                                      asOverlay
                                      toggleAs={(fprops) => (
                                        <BFButton
                                          appearance="outline"
                                          size={"sm"}
                                          {...fprops}
                                        >
                                          <BfIcon
                                            {...DefaultIcons.MORE}
                                            size="xs"
                                          />
                                        </BFButton>
                                      )}
                                      items={[
                                        {
                                          type: "button",
                                          text: i18n.t(
                                            "ra:Invoice.addDiscount",
                                            "Skonto hinzufügen"
                                          ),
                                          disabled:
                                            Boolean(payedDate) ||
                                            props.disableAll ||
                                            paymentTypeDisabledEdit,
                                          onSelect: () => {
                                            ModalManager.show({
                                              noPadding: true,
                                              content: (
                                                state,
                                                setStates,
                                                closeModal
                                              ) => (
                                                <RAInvoicePaymentSkontoForm
                                                  onClose={closeModal}
                                                  onSubmit={(skontoField) => {
                                                    props.form.change(
                                                      `paymentPlan[0].discounts`,
                                                      [
                                                        ...(fields.value[0]
                                                          .discounts || []),
                                                        skontoField,
                                                      ]
                                                    );
                                                  }}
                                                />
                                              ),
                                            });
                                          },
                                        },
                                        {
                                          type: "button",
                                          text: i18n.t(
                                            "ra:Invoice.splitPayment",
                                            "Rechnungsbetrag aufteilen"
                                          ),
                                          disabled:
                                            Boolean(payedDate) ||
                                            props.disableAll ||
                                            paymentTypeDisabledEdit,
                                          onSelect: () => {
                                            ModalManager.show({
                                              noPadding: true,

                                              content: (
                                                state,
                                                setStates,
                                                closeModal
                                              ) => (
                                                <RAInvoicePaymentSplitForm
                                                  onClose={closeModal}
                                                  onSubmit={(
                                                    payments: PaymentEntry[]
                                                  ) => {
                                                    props.form.change(
                                                      `${
                                                        props.prefix || ""
                                                      }paymentPlan`,
                                                      payments
                                                    );
                                                  }}
                                                  title={i18n.t(
                                                    "ra:Invoice.splitPayment",
                                                    "Rechnungsbetrag aufteilen"
                                                  )}
                                                  submitText={i18n.t(
                                                    "ra:Invoice.splitPayment",
                                                    "Rechnungsbetrag aufteilen"
                                                  )}
                                                  currency={currency}
                                                  initialAmount={
                                                    fields.value[0].amount
                                                  }
                                                  initialDate={
                                                    fields.value[0].date
                                                  }
                                                  initialUsage={
                                                    fields.value[0].usage
                                                  }
                                                  variables={[
                                                    ...(isDefined(objectId)
                                                      ? [
                                                          {
                                                            key: i18n.t(
                                                              "ra:InvoiceForm.variables.objectName",
                                                              "Objektname"
                                                            ),
                                                            value:
                                                              OrgaStruct.getObject(
                                                                objectId
                                                              ).displayName,
                                                          },
                                                          {
                                                            key: i18n.t(
                                                              "ra:InvoiceForm.variables.objectId",
                                                              "Objektnummer"
                                                            ),
                                                            value:
                                                              OrgaStruct.getObject(
                                                                objectId
                                                              ).id,
                                                          },
                                                        ]
                                                      : []),
                                                    ...(isDefined(entity)
                                                      ? [
                                                          {
                                                            key: i18n.t(
                                                              "ra:InvoiceForm.variables.entity",
                                                              "Gesellschaft"
                                                            ),
                                                            value:
                                                              OrgaStruct.getEntity(
                                                                entity
                                                              ).displayName,
                                                          },
                                                        ]
                                                      : []),
                                                    ...(isDefined(invoiceId)
                                                      ? [
                                                          {
                                                            key: i18n.t(
                                                              "ra:InvoiceForm.variables.invoiceId",
                                                              "Rechnungsnummer"
                                                            ),
                                                            value: invoiceId,
                                                          },
                                                        ]
                                                      : []),
                                                  ]}
                                                />
                                              ),
                                            });
                                          },
                                        },
                                      ]}
                                    />
                                  )}
                                </FormFieldValues>
                              </div>
                            </div>
                            <FormFieldValues
                              names={[`${props.prefix || ""}paymentType`]}
                            >
                              {([paymentType]) => {
                                const modeOfPayment =
                                  InvoiceStruct.getModeOfPayment(
                                    props.invoice.data.direction,
                                    paymentType
                                  );
                                const isBankTransfer =
                                  modeOfPayment?.kind === "bankTransfer";
                                if (!isBankTransfer) {
                                  return null;
                                }
                                return (
                                  <div className={`form-row`}>
                                    <ExtractField
                                      ignoreCompare={props.ignoreCompare}
                                      name={`${
                                        props.prefix || ""
                                      }paymentPlan[0].createPaymentObject`}
                                      invoice={props.invoice}
                                      onHighlight={props.onHighlight}
                                      field={"createPaymentObject"}
                                      initialValue={true}
                                    >
                                      {({ input, meta }) => (
                                        <BFCheckbox
                                          disabled={
                                            Boolean(payedDate) ||
                                            props.disableAll
                                          }
                                          checked={input.value}
                                          onChange={(_, checked) =>
                                            input.onChange(checked)
                                          }
                                        >
                                          {i18n.t(
                                            "ra:Invoice.createPaymentObject",
                                            "Zahlung ausführen"
                                          )}
                                        </BFCheckbox>
                                      )}
                                    </ExtractField>
                                  </div>
                                );
                              }}
                            </FormFieldValues>

                            <SkontoEntries
                              name={`${
                                props.prefix || ""
                              }paymentPlan[0].discounts`}
                              disabled={Boolean(payedDate) || props.disableAll}
                            />
                            {Boolean(payedDate) && (
                              <div className={`payed-date`}>
                                <strong>
                                  {i18n.t(
                                    "ra:InvoiceForm.payedDate",
                                    "Bezahlt am"
                                  )}
                                </strong>{" "}
                                {StringUtils.formatDate(payedDate)}
                              </div>
                            )}
                          </div>
                        )}
                      </FormFieldValues>
                    );
                  } else {
                    return (
                      <PaymentSection
                        {...props}
                        title={i18n.t("ra:Invoice.PaymentPlan", "Zahlungsplan")}
                        allowSkonto
                        amountLabel={i18n.t(
                          "ra:InvoiceForm.paymentEntryAmount",
                          "Zahlungsbetrag"
                        )}
                        sumLabel={i18n.t(
                          "ra:InvoiceForm.invoiceSum",
                          "Rechnungsbetrag"
                        )}
                        addButtonText={i18n.t(
                          "ra:Invoice.addPaymentEntry",
                          "Zahlung hinzufügen"
                        )}
                        paymentTypeDisabledEdit={paymentTypeDisabledEdit}
                        currency={currency}
                        fieldName={`${props.prefix || ""}paymentPlan`}
                        initialValue={[]}
                      />
                    );
                  }
                }}
              </FieldArray>
            </div>

            <FormFieldValues
              names={[
                `${props.prefix || ""}installment`,
                `${props.prefix || ""}costCenter`,
                `${props.prefix || ""}objectId`,
                `${props.prefix || ""}entity`,
                `${props.prefix || ""}invoiceId`,
                `${props.prefix || ""}paymentPlan`,
              ]}
            >
              {([
                installment,
                costCenter,
                objectId,
                entity,
                invoiceId,
                paymentPlan,
              ]) => (
                <>
                  {(installment || []).length > 0 && (
                    <PaymentSection
                      {...props}
                      title={i18n.t(
                        "ra:Invoice.installment",
                        "Abschlagszahlungen"
                      )}
                      amountLabel={i18n.t(
                        "ra:InvoiceForm.installmentEntryAmount",
                        "Abschlagsbetrag"
                      )}
                      sumLabel={i18n.t(
                        "ra:InvoiceForm.installmentSum",
                        "Summe Abschlagszahlungen"
                      )}
                      addButtonText={i18n.t(
                        "ra:Invoice.addInstallmentEntry",
                        "Abschlagszahlung hinzufügen"
                      )}
                      currency={currency}
                      fieldName={`${props.prefix || ""}installment`}
                      initialValue={[]}
                    />
                  )}

                  {(costCenter || []).length > 0 && (
                    <div>
                      <RAInvoiceCostcenterForm
                        invoice={props.invoice}
                        ignoreCompare={props.ignoreCompare}
                        disableAll={props.disableAll}
                        withDescription
                        prefix={props.prefix}
                      />
                    </div>
                  )}

                  {((installment || []).length === 0 ||
                    (costCenter || []).length === 0) && (
                    <div
                      className={`add-actions __flex __justify-center padding-top-20`}
                    >
                      {(installment || []).length === 0 && (
                        <BFButton
                          appearance="outline"
                          size="xs"
                          disabled={props.disableAll}
                          onClick={() => {
                            ModalManager.show({
                              noPadding: true,
                              content: (state, setStates, closeModal) => (
                                <RAInvoicePaymentSplitForm
                                  onClose={closeModal}
                                  onSubmit={(payments: PaymentEntry[]) => {
                                    props.form.change(
                                      `${props.prefix || ""}installment`,
                                      payments
                                    );
                                  }}
                                  title={i18n.t(
                                    "ra:Invoice.planInstallment",
                                    "Abschlagszahlungen planen"
                                  )}
                                  submitText={i18n.t(
                                    "ra:Invoice.addInstallment",
                                    "Abschlagszahlungen hinzufügen"
                                  )}
                                  currency={currency}
                                  initialAmount={0}
                                  initialDate={paymentPlan[0].date}
                                  initialUsage={paymentPlan[0].usage}
                                  variables={[
                                    ...(isDefined(objectId)
                                      ? [
                                          {
                                            key: i18n.t(
                                              "ra:InvoiceForm.variables.objectName",
                                              "Objektname"
                                            ),
                                            value:
                                              OrgaStruct.getObject(objectId)
                                                ?.displayName || "-",
                                          },
                                          {
                                            key: i18n.t(
                                              "ra:InvoiceForm.variables.objectId",
                                              "Objektnummer"
                                            ),
                                            value:
                                              OrgaStruct.getObject(objectId)
                                                ?.id || "-",
                                          },
                                        ]
                                      : []),
                                    ...(isDefined(entity)
                                      ? [
                                          {
                                            key: i18n.t(
                                              "ra:InvoiceForm.variables.entity",
                                              "Gesellschaft"
                                            ),
                                            value:
                                              OrgaStruct.getEntity(entity)
                                                ?.displayName || "-",
                                          },
                                        ]
                                      : []),
                                    ...(isDefined(invoiceId)
                                      ? [
                                          {
                                            key: i18n.t(
                                              "ra:InvoiceForm.variables.invoiceId",
                                              "Rechnungsnummer"
                                            ),
                                            value: invoiceId || "-",
                                          },
                                        ]
                                      : []),
                                  ]}
                                />
                              ),
                            });
                          }}
                        >
                          {i18n.t(
                            "ra:Invoice.addInstallment",
                            "Abschlagszahlungen hinzufügen"
                          )}
                        </BFButton>
                      )}
                      <FormFieldValues
                        names={[`${props.prefix || ""}paymentPlan`]}
                      >
                        {([paymentPlan]) => (
                          <>
                            {paymentPlan.length === 1 && (
                              <BFButton
                                appearance="outline"
                                size="xs"
                                disabled={
                                  Boolean(paymentPlan[0].payedDate) ||
                                  props.disableAll ||
                                  paymentTypeDisabledEdit
                                }
                                onClick={() => {
                                  ModalManager.show({
                                    noPadding: true,
                                    content: (state, setStates, closeModal) => (
                                      <RAInvoicePaymentSplitForm
                                        onClose={closeModal}
                                        onSubmit={(
                                          payments: PaymentEntry[]
                                        ) => {
                                          props.form.change(
                                            `${props.prefix || ""}paymentPlan`,
                                            payments
                                          );
                                        }}
                                        title={i18n.t(
                                          "ra:Invoice.splitPayment",
                                          "Rechnungsbetrag aufteilen"
                                        )}
                                        submitText={i18n.t(
                                          "ra:Invoice.splitPayment",
                                          "Rechnungsbetrag aufteilen"
                                        )}
                                        currency={currency}
                                        initialAmount={paymentPlan[0].amount}
                                        initialDate={paymentPlan[0].date}
                                        initialUsage={paymentPlan[0].usage}
                                        variables={[
                                          ...(isDefined(objectId)
                                            ? [
                                                {
                                                  key: i18n.t(
                                                    "ra:InvoiceForm.variables.objectName",
                                                    "Objektname"
                                                  ),
                                                  value:
                                                    OrgaStruct.getObject(
                                                      objectId
                                                    )?.displayName || "-",
                                                },
                                                {
                                                  key: i18n.t(
                                                    "ra:InvoiceForm.variables.objectId",
                                                    "Objektnummer"
                                                  ),
                                                  value:
                                                    OrgaStruct.getObject(
                                                      objectId
                                                    )?.id || "-",
                                                },
                                              ]
                                            : []),
                                          ...(isDefined(entity)
                                            ? [
                                                {
                                                  key: i18n.t(
                                                    "ra:InvoiceForm.variables.entity",
                                                    "Gesellschaft"
                                                  ),
                                                  value:
                                                    OrgaStruct.getEntity(entity)
                                                      ?.displayName || "-",
                                                },
                                              ]
                                            : []),
                                          ...(isDefined(invoiceId)
                                            ? [
                                                {
                                                  key: i18n.t(
                                                    "ra:InvoiceForm.variables.invoiceId",
                                                    "Rechnungsnummer"
                                                  ),
                                                  value: invoiceId || "-",
                                                },
                                              ]
                                            : []),
                                        ]}
                                      />
                                    ),
                                  });
                                }}
                              >
                                {i18n.t(
                                  "ra:Invoice.splitPayment",
                                  "Rechnungsbetrag aufteilen"
                                )}
                              </BFButton>
                            )}
                            {/* <
                    <FormFieldValues
                      names={[`${props.prefix || ""}paymentPlan[0].payedDate`]}
                    >
                      {([payedDate]) => ( */}

                            {(costCenter || []).length === 0 && (
                              <BFButton
                                disabled={props.disableAll}
                                appearance="outline"
                                size="xs"
                                onClick={() => {
                                  props.form.change(
                                    `${props.prefix || ""}costCenter`,
                                    [
                                      {
                                        info: "",
                                        objectId: objectId || null,
                                        value: paymentPlan.reduce(
                                          (acc, curr) =>
                                            acc + (curr.amount || 0),
                                          0
                                        ),
                                      },
                                    ]
                                  );
                                }}
                              >
                                {i18n.t(
                                  "ra:Invoice.addCostcenter",
                                  "Unterkategorien aufteilen"
                                )}
                              </BFButton>
                            )}
                          </>
                        )}
                      </FormFieldValues>
                    </div>
                  )}
                </>
              )}
            </FormFieldValues>
          </div>
        );
      }}
    </FormFieldValues>
  );
};

export default RAInvoicePaymentFields;

const SkontoEntries = (props: { name: string; disabled: boolean }) => {
  return (
    <FieldArray<SkontoFields> name={props.name}>
      {({ fields }) => {
        if (fields.length === 0) {
          return null;
        }

        return (
          <div className={`skonto-entries`}>
            {fields.map((name, index) => (
              <div className={`skonto-entry`}>
                <div className={`data`}>
                  <FormFieldValues
                    names={[
                      `${name}.discountPercentage`,
                      `${name}.daysForPayment`,
                    ]}
                  >
                    {([discountPercentage, daysForPayment]) => (
                      <>
                        <strong>
                          {StringUtils.formatPercent(discountPercentage)}
                        </strong>{" "}
                        {i18n.t(
                          "ra:InvoiceForm.discountForPaymentLabel",
                          "Skonto bei Zahlung in"
                        )}{" "}
                        <strong>
                          {daysForPayment}{" "}
                          {i18n.t(
                            "ra:InvoiceForm.discountForPaymentLabelDays",
                            "Tagen"
                          )}
                        </strong>
                      </>
                    )}
                  </FormFieldValues>
                </div>
                <BFButton
                  disabled={props.disabled}
                  noPadding
                  appearance="link"
                  size="xs"
                  onClick={() => {
                    fields.remove(index);
                  }}
                >
                  <BfIcon {...DefaultIcons.CLOSE} size="xxs" />
                </BFButton>
              </div>
            ))}
          </div>
        );
      }}
    </FieldArray>
  );
};

const PaymentSection = (props: {
  disableAll?: boolean;
  ignoreCompare?: boolean;
  prefix?: string;
  title: string;
  amountLabel: string;
  sumLabel: string;
  fieldName: string;
  initialValue: PaymentEntry[];
  paymentTypeDisabledEdit?: boolean;
  invoice: Invoice;
  form: FormApi;
  onHighlight: (highlight?: PDFHighlight | PDFHighlight[]) => void;

  uploadForm?: boolean;

  addButtonText: string;
  allowSkonto?: boolean;
  currency: string;
}) => {
  const [initialData] = useState(props.initialValue);
  return (
    <FieldArray<SkontoPaymentFormEntry>
      name={props.fieldName}
      initialValue={initialData as any}
    >
      {({ fields }) => (
        <BFSection
          collapsible
          title={props.title}
          small
          className={`margin-top-20 padding-10`}
        >
          <>
            <div className={`payment-entries`}>
              {fields.map((name, index) => {
                return (
                  <FormFieldValues names={[`${name}.payedDate`]} key={name}>
                    {([payedDate]) => (
                      <div className={`payment-entry`}>
                        <div className={`__flex`}>
                          <div className={`field __flex-1`}>
                            <ExtractField
                              ignoreCompare={props.ignoreCompare}
                              name={`${name}.amount`}
                              validate={FormValidators.compose(
                                FormValidators.required()
                              )}
                              invoice={props.invoice}
                              onHighlight={props.onHighlight}
                              field={INVOICE_EXTRACT_KEYS.paymentAmountTotal}
                            >
                              {({ input, meta }) => (
                                <BFInput
                                  disabled={
                                    Boolean(payedDate) || props.disableAll
                                  }
                                  prefix={StringUtils.getCurrencySymbol(
                                    props.currency
                                  )}
                                  type={"priceInput"}
                                  label={
                                    index === 0 ? props.amountLabel + "*" : null
                                  }
                                  {...FormValidators.getValidation(meta)}
                                  {...input}
                                />
                              )}
                            </ExtractField>
                          </div>
                          <div className={`field __flex-1`}>
                            <ExtractField
                              ignoreCompare={props.ignoreCompare}
                              name={`${name}.date`}
                              validate={FormValidators.compose(
                                FormValidators.required()
                              )}
                              invoice={props.invoice}
                              onHighlight={props.onHighlight}
                              field={INVOICE_EXTRACT_KEYS.paymentDateDue}
                            >
                              {({ input, meta }) => (
                                <BFDatefield
                                  disabled={
                                    Boolean(payedDate) ||
                                    props.disableAll ||
                                    props.paymentTypeDisabledEdit
                                  }
                                  label={
                                    index === 0
                                      ? i18n.t(
                                          "ra:InvoiceForm.dueDate",
                                          "Fälligkeitsdatum"
                                        ) + "*"
                                      : null
                                  }
                                  preventNull
                                  {...FormValidators.getValidation(meta)}
                                  {...input}
                                />
                              )}
                            </ExtractField>
                          </div>
                          <div className={`field __flex-1`}>
                            <ExtractField
                              ignoreCompare={props.ignoreCompare}
                              name={`${name}.usage`}
                              validate={FormValidators.compose(
                                FormValidators.required()
                              )}
                              invoice={props.invoice}
                              onHighlight={props.onHighlight}
                              field={INVOICE_EXTRACT_KEYS.usage}
                            >
                              {({ input, meta }) => (
                                <BFInput
                                  disabled={
                                    Boolean(payedDate) || props.disableAll
                                  }
                                  label={
                                    index === 0 ? (
                                      <span>
                                        {i18n.t(
                                          "ra:InvoiceForm.usage",
                                          "Verwendungszweck"
                                        ) + "*"}

                                        <FormFieldValues
                                          names={[
                                            `${props.prefix || ""}objectId`,
                                            `${props.prefix || ""}entity`,
                                            `${props.prefix || ""}invoiceId`,
                                          ]}
                                        >
                                          {([objectId, entity, invoiceId]) => (
                                            <BFButton
                                              className={`margin-left-10`}
                                              appearance="link"
                                              noPadding
                                              inline
                                              onClick={() => {
                                                ModalManager.show({
                                                  noPadding: true,
                                                  content: (
                                                    state,
                                                    setStates,
                                                    close
                                                  ) => (
                                                    <RAInvoicePaymentUsageEditForm
                                                      entries={fields.value}
                                                      onClose={close}
                                                      onSubmit={(
                                                        newEntries
                                                      ) => {
                                                        newEntries.forEach(
                                                          (entry, i) => {
                                                            fields.update(
                                                              index + i,
                                                              {
                                                                ...fields.value[
                                                                  index + i
                                                                ],
                                                                usage:
                                                                  entry.usage,
                                                              }
                                                            );
                                                          }
                                                        );
                                                      }}
                                                      initialUsage={input.value}
                                                      variables={[
                                                        ...(isDefined(objectId)
                                                          ? [
                                                              {
                                                                key: i18n.t(
                                                                  "ra:InvoiceForm.variables.objectName",
                                                                  "Objektname"
                                                                ),
                                                                value:
                                                                  OrgaStruct.getObject(
                                                                    objectId
                                                                  ).displayName,
                                                              },
                                                              {
                                                                key: i18n.t(
                                                                  "ra:InvoiceForm.variables.objectId",
                                                                  "Objektnummer"
                                                                ),
                                                                value:
                                                                  OrgaStruct.getObject(
                                                                    objectId
                                                                  ).id,
                                                              },
                                                            ]
                                                          : []),
                                                        ...(isDefined(entity)
                                                          ? [
                                                              {
                                                                key: i18n.t(
                                                                  "ra:InvoiceForm.variables.entity",
                                                                  "Gesellschaft"
                                                                ),
                                                                value:
                                                                  OrgaStruct.getEntity(
                                                                    entity
                                                                  ).displayName,
                                                              },
                                                            ]
                                                          : []),
                                                        ...(isDefined(invoiceId)
                                                          ? [
                                                              {
                                                                key: i18n.t(
                                                                  "ra:InvoiceForm.variables.invoiceId",
                                                                  "Rechnungsnummer"
                                                                ),
                                                                value:
                                                                  invoiceId,
                                                              },
                                                            ]
                                                          : []),
                                                      ]}
                                                    />
                                                  ),
                                                });
                                              }}
                                            >
                                              <BfIcon
                                                data="cog-1"
                                                type="light"
                                                size="xxs"
                                              />
                                            </BFButton>
                                          )}
                                        </FormFieldValues>
                                      </span>
                                    ) : null
                                  }
                                  {...FormValidators.getValidation(meta)}
                                  {...input}
                                />
                              )}
                            </ExtractField>
                          </div>

                          <FormFieldValues
                            names={[`${props.prefix || ""}paymentType`]}
                          >
                            {([paymentType]) => {
                              const modeOfPayment =
                                InvoiceStruct.getModeOfPayment(
                                  props.invoice.data.direction,
                                  paymentType
                                );
                              const isBankTransfer =
                                modeOfPayment?.kind === "bankTransfer";
                              if (!isBankTransfer) {
                                return null;
                              }
                              return (
                                <div
                                  className={classNames(`field __flex-1`, {
                                    "with-padding-top": index === 0,
                                  })}
                                >
                                  <ExtractField
                                    ignoreCompare={props.ignoreCompare}
                                    name={`${name}.createPaymentObject`}
                                    validate={FormValidators.compose(
                                      FormValidators.required()
                                    )}
                                    invoice={props.invoice}
                                    onHighlight={props.onHighlight}
                                    field={"createPaymentObject"}
                                    initialValue={true}
                                  >
                                    {({ input, meta }) => (
                                      <BFCheckbox
                                        disabled={
                                          Boolean(payedDate) || props.disableAll
                                        }
                                        checked={input.value}
                                        onChange={(_, checked) =>
                                          input.onChange(checked)
                                        }
                                      >
                                        {i18n.t(
                                          "ra:Invoice.createPaymentObject",
                                          "Zahlung ausführen"
                                        )}
                                      </BFCheckbox>
                                    )}
                                  </ExtractField>
                                </div>
                              );
                            }}
                          </FormFieldValues>
                          <div
                            className={classNames(`button-container`, {
                              "no-padding": index !== 0,
                            })}
                          >
                            <BFDropdown
                              label="."
                              asOverlay
                              toggleAs={(fprops) => (
                                <BFButton
                                  appearance="outline"
                                  size={"sm"}
                                  {...fprops}
                                >
                                  <BfIcon {...DefaultIcons.MORE} size="xs" />
                                </BFButton>
                              )}
                              items={[
                                ...(props.allowSkonto
                                  ? [
                                      {
                                        type: "button",
                                        text: i18n.t(
                                          "ra:Invoice.addDiscount",
                                          "Skonto hinzufügen"
                                        ),
                                        disabled:
                                          Boolean(payedDate) ||
                                          props.disableAll ||
                                          props.paymentTypeDisabledEdit,
                                        onSelect: () => {
                                          ModalManager.show({
                                            noPadding: true,
                                            content: (
                                              state,
                                              setStates,
                                              closeModal
                                            ) => (
                                              <RAInvoicePaymentSkontoForm
                                                onClose={closeModal}
                                                onSubmit={(skontoField) => {
                                                  props.form.change(
                                                    `${props.fieldName}[${index}].discounts`,
                                                    [
                                                      ...(fields.value[index]
                                                        .discounts || []),
                                                      skontoField,
                                                    ]
                                                  );
                                                }}
                                              />
                                            ),
                                          });
                                        },
                                      } as DropdownButton,
                                    ]
                                  : []),
                                {
                                  type: "button",
                                  text: i18n.t(
                                    "ra:Invoice.removePaymentEntry",
                                    "Zahlung entfernen"
                                  ),
                                  disabled:
                                    Boolean(payedDate) ||
                                    props.disableAll ||
                                    props.paymentTypeDisabledEdit,
                                  onSelect: () => {
                                    fields.remove(index);
                                  },
                                },
                                {
                                  type: "button",
                                  text: i18n.t(
                                    "ra:Invoice.copyPaymentAmount",
                                    "Zahlungsbetrag kopieren"
                                  ),
                                  disabled:
                                    props.disableAll ||
                                    props.paymentTypeDisabledEdit,
                                  onSelect: () => {
                                    const amount = fields.value[index].amount;
                                    // update all other payment entries with the same amount, when entry.payedDate is not set
                                    fields.forEach((_, i) => {
                                      if (i !== index) {
                                        const entry = fields.value[i];
                                        if (!entry.payedDate) {
                                          fields.update(i, {
                                            ...entry,
                                            amount: amount,
                                          });
                                        }
                                      }
                                    });
                                  },
                                },
                              ]}
                            />
                          </div>
                        </div>
                        {props.allowSkonto && (
                          <SkontoEntries
                            name={`${name}.discounts`}
                            disabled={
                              Boolean(payedDate) ||
                              props.disableAll ||
                              props.paymentTypeDisabledEdit
                            }
                          />
                        )}
                        {Boolean(payedDate) && (
                          <div className={`payed-date`}>
                            <strong>
                              {i18n.t("ra:InvoiceForm.payedDate", "Bezahlt am")}
                            </strong>{" "}
                            {StringUtils.formatDate(payedDate)}
                          </div>
                        )}
                      </div>
                    )}
                  </FormFieldValues>
                );
              })}
            </div>

            <div className={`__flex __justify-center`}>
              <BFButton
                disabled={props.disableAll || props.paymentTypeDisabledEdit}
                appearance="link"
                size="sm"
                onClick={() => {
                  fields.push({
                    amount: 0,
                    date: new Date(),
                    usage: "",
                  });
                }}
              >
                {props.addButtonText}
              </BFButton>
            </div>
            <div className={`sum-field`}>
              <strong>{props.sumLabel}: </strong>
              <span>
                {StringUtils.formatCurrency(
                  fields.value.reduce(
                    (acc, curr) => acc + (curr.amount || 0),
                    0
                  ),
                  undefined,
                  undefined,
                  props.currency
                )}
              </span>
            </div>
          </>
        </BFSection>
      )}
    </FieldArray>
  );
};
